import {
	FormEvent,
	KeyboardEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	ArrowRight,
	ArrowUp,
	Check,
	CornerDownRight,
	LoaderCircle,
	RefreshCcw,
	X,
} from 'lucide-react'
import { observer } from 'mobx-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useLocation } from 'react-router-dom'
import { useIonRouter } from '@ionic/react'
import { APP_ENV } from '@assets/environment/appEnv'
import {
	type PreviewExercise,
	type WorkoutPreview,
	type WorkoutPreviewRecord,
} from '@entities/AIMessenger/api'
import { ExerciseProxy } from '@entities/exercise'
import { type Training } from '@entities/training'
import {
	buildPromptChips,
	CoachChatMessage,
	messagesAfterLatestSeparator,
	parsePinnedContext,
	pinnedContextLabel,
	PinnedChatContext,
} from '@entities/AIMessenger/model/coachChat'
import {
	coachChatStore,
	type ChatConnectionState,
} from '@entities/AIMessenger/model/coachChat.store'
import { Content, DefaultPage, Footer } from '@shared/layout/DefaultPage'
import { AppNavbar } from '@shared/ui/AppNavbar'
import { ROUTES } from '@shared/constants/routes'
import { createSearchParams, generateFilters } from '@shared/helpers/generateSearchParams'
import { getMediaRoleUrl } from '@shared/helpers/getMediaUrl'
import { TrainingDetailsSurface } from '@widgets/trainingDetailsOverlay'
import { TextShimmer } from '@/components/ui/text-shimmer'
import CoachHeaderImage from '@assets/images/coach.png'
import { buildChatDebugCopyTexts, buildChatSessionDebugCopyText } from '../lib/chat-debug'
import { ChatDebugCopyButton, ChatSessionDebugBar } from './chat-debug-tools'
import './coach-chat.css'

type ExerciseMediaCache = Record<string, string | null>
type SendTextOptions = {
	showUserMessage?: boolean
}
type PreviewExerciseWithMedia = PreviewExercise & {
	media?: Record<string, unknown> | null
	image_url?: string | null
	thumbnail_url?: string | null
}

export const ChatPage = observer(() => {
	const location = useLocation()
	const nav = useIonRouter()
	const contentRef = useRef<HTMLIonContentElement | null>(null)
	const autoSentPinnedContextRef = useRef<string | null>(null)
	const preserveScrollAfterOlderLoadRef = useRef(false)
	const [draft, setDraft] = useState('')
	const [selectedPreview, setSelectedPreview] = useState<WorkoutPreviewRecord | null>(null)
	const [exerciseMediaByKey, setExerciseMediaByKey] = useState<ExerciseMediaCache>({})
	const pendingExerciseMediaKeys = useRef<Set<string>>(new Set())
	const [pinnedContext, setPinnedContext] = useState<PinnedChatContext | null>(() =>
		parsePinnedContext(location.search)
	)
	const messages = coachChatStore.messages
	const activeMessages = useMemo(() => messagesAfterLatestSeparator(messages), [messages])
	const latestPendingPreview = coachChatStore.latestPendingPreview
	const isBusy = coachChatStore.isBusy
	const state = coachChatStore.state
	const sessionId = coachChatStore.sessionId
	const session = coachChatStore.session
	const lastChatEndpoint = coachChatStore.lastChatEndpoint
	const lastChatService = coachChatStore.lastChatService
	const isEmptyConversation = messages.length === 0 && state !== 'connecting'
	const debugCopyTexts = useMemo(
		() =>
			APP_ENV.IS_DEV
				? buildChatDebugCopyTexts(messages, {
						sessionId,
						session,
						chatEndpoint: lastChatEndpoint,
						chatService: lastChatService,
					})
				: {},
		[lastChatEndpoint, lastChatService, messages, session, sessionId]
	)
	const getSessionDebugCopyText = useCallback(
		() =>
			buildChatSessionDebugCopyText({
				sessionId,
				session,
				chatEndpoint: lastChatEndpoint,
				chatService: lastChatService,
			}),
		[lastChatEndpoint, lastChatService, session, sessionId]
	)

	useEffect(() => {
		setPinnedContext(parsePinnedContext(location.search))
	}, [location.search])

	useEffect(() => {
		const exercisesToResolve = uniquePreviewExercises(collectPreviewExercises(messages)).filter(
			exercise => {
				const key = getPreviewExerciseMediaKey(exercise)
				return (
					key &&
					!(key in exerciseMediaByKey) &&
					!pendingExerciseMediaKeys.current.has(key) &&
					!getPreviewExerciseDirectMediaUrl(exercise)
				)
			}
		)

		if (!exercisesToResolve.length) return

		let cancelled = false
		exercisesToResolve.forEach(exercise => {
			pendingExerciseMediaKeys.current.add(getPreviewExerciseMediaKey(exercise))
		})

		const resolveMedia = async () => {
			const entries = await Promise.all(
				exercisesToResolve.map(async exercise => {
					const key = getPreviewExerciseMediaKey(exercise)
					const mediaUrl = await resolvePreviewExerciseMediaUrl(exercise)
					return [key, mediaUrl] as const
				})
			)

			entries.forEach(([key]) => pendingExerciseMediaKeys.current.delete(key))
			if (cancelled) return

			setExerciseMediaByKey(previous => {
				const next = { ...previous }
				entries.forEach(([key, mediaUrl]) => {
					next[key] = mediaUrl
				})
				return next
			})
		}

		void resolveMedia()

		return () => {
			cancelled = true
		}
	}, [exerciseMediaByKey, messages])

	useLayoutEffect(() => {
		if (preserveScrollAfterOlderLoadRef.current) return
		requestAnimationFrame(() => {
			void contentRef.current?.scrollToBottom(state === 'connecting' ? 0 : 180)
		})
	}, [messages.length, state])

	const loadOlderMessages = useCallback(async () => {
		if (!coachChatStore.hasMore || coachChatStore.isLoadingOlder) return
		const scrollElement = await contentRef.current?.getScrollElement()
		const previousScrollHeight = scrollElement?.scrollHeight ?? 0
		const previousScrollTop = scrollElement?.scrollTop ?? 0
		preserveScrollAfterOlderLoadRef.current = true
		try {
			await coachChatStore.loadOlderMessages()
			requestAnimationFrame(async () => {
				const nextScrollElement = await contentRef.current?.getScrollElement()
				if (nextScrollElement) {
					const delta = nextScrollElement.scrollHeight - previousScrollHeight
					await contentRef.current?.scrollToPoint(0, previousScrollTop + delta, 0)
				}
				preserveScrollAfterOlderLoadRef.current = false
			})
		} catch {
			preserveScrollAfterOlderLoadRef.current = false
		}
	}, [])

	const sendText = useCallback(
		(text: string, options: SendTextOptions = {}) => {
			const accepted = coachChatStore.sendText(text, {
				...options,
				pinnedContext,
			})
			if (accepted && (options.showUserMessage ?? true)) {
				setDraft('')
			}
		},
		[pinnedContext]
	)

	const handleSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault()
			void sendText(draft)
		},
		[draft, sendText]
	)

	useEffect(() => {
		const initialMessage = pinnedContext?.initialMessage?.trim()
		if (!initialMessage || state !== 'ready' || isBusy) return

		const autoSendKey = [
			pinnedContext?.exerciseId,
			pinnedContext?.workoutId,
			pinnedContext?.workoutHistoryId,
			initialMessage,
		]
			.filter(Boolean)
			.join(':')
		if (autoSentPinnedContextRef.current === autoSendKey) return

		autoSentPinnedContextRef.current = autoSendKey
		sendText(initialMessage)
	}, [isBusy, pinnedContext, sendText, state])

	const startNewSession = useCallback(() => {
		coachChatStore.startNewSession()
	}, [])

	const approvePreview = useCallback((preview: WorkoutPreviewRecord) => {
		setSelectedPreview(null)
		coachChatStore.approvePreview(preview)
	}, [])

	const retryLastTurn = useCallback(() => {
		coachChatStore.retryLastTurn(pinnedContext)
	}, [pinnedContext])

	return (
		<DefaultPage
			noPadding
			className={[
				'coach-chat-page',
				pinnedContext ? 'has-pinned-context' : '',
				APP_ENV.IS_DEV ? 'has-chat-debug' : '',
			]
				.filter(Boolean)
				.join(' ')}
		>
			<Content ref={contentRef} className='coach-chat-content' scrollY>
				<main className='coach-chat-shell'>
					<AppNavbar
						className='coach-chat-app-navbar'
						style={{
							height: 'calc(var(--app-safe-area-top) + 86px)',
							paddingBottom: '18px',
						}}
						logoAriaLabel='Открыть главный экран'
						logoTap={{ enabled: false }}
						onLogoClick={() => {
							nav.push(ROUTES.overall.main)
							return true
						}}
						centerSlot={<CoachHeaderSummary state={state} />}
						centerSlotClassName='coach-chat-navbar-title-slot'
						centerSlotStyle={{
							bottom: '18px',
							height: '48px',
							left: '116px',
							right: '136px',
						}}
						avatarActionSlot={
							<CoachSessionShortcut
								state={state}
								isStartingNewSession={coachChatStore.isStartingNewSession}
								onStartNewSession={startNewSession}
							/>
						}
					/>

					<LoadOlderControl
						hasMore={coachChatStore.hasMore}
						isLoading={coachChatStore.isLoadingOlder}
						onLoad={loadOlderMessages}
					/>

					<section
						className={`coach-chat-list ${isEmptyConversation ? 'is-empty' : ''}`}
						aria-label='Сообщения тренера'
					>
						{isEmptyConversation ? <EmptyConversation onSelect={sendText} /> : null}
						{messages.map(message => (
							<MessageBlock
								key={message.id}
								message={message}
								activePreviewAction={coachChatStore.activePreviewAction}
								onOpenPreview={setSelectedPreview}
								onRetry={retryLastTurn}
								exerciseMediaByKey={exerciseMediaByKey}
								debugCopyText={APP_ENV.IS_DEV ? debugCopyTexts[message.id] : null}
							/>
						))}
						{state === 'connecting' ? (
							<StreamingMessageBlock label='Загружаем историю' />
						) : null}
					</section>
				</main>
			</Content>
			<Footer className='coach-chat-footer'>
				{!isEmptyConversation ? (
					<PromptChipRail
						pinnedContext={pinnedContext}
						pendingPreview={latestPendingPreview}
						messages={activeMessages}
						onSelect={sendText}
						isBusy={isBusy}
					/>
				) : null}
				{pinnedContext ? (
					<PinnedContextAttachment
						context={pinnedContext}
						onClear={() => setPinnedContext(null)}
					/>
				) : null}
				<ChatSessionDebugBar
					enabled={APP_ENV.IS_DEV}
					sessionId={sessionId}
					copyText={getSessionDebugCopyText}
					isConnecting={state === 'connecting'}
				/>
				<Composer
					value={draft}
					isBusy={isBusy}
					onChange={setDraft}
					onSubmit={handleSubmit}
				/>
			</Footer>
			{selectedPreview ? (
				<TrainingDetailsSurface
					workoutId={previewWorkoutId(selectedPreview)}
					isValidWorkoutId
					initialTraining={buildPreviewTraining(selectedPreview, exerciseMediaByKey)}
					muscleItems={[]}
					onDismiss={() => setSelectedPreview(null)}
					presentation={{
						headerTitle: 'План тренировки',
						sourceLabel: 'Тренерский план',
						sourceBadge: previewTabLabel(selectedPreview.status),
						previewMeta: previewDetailsMeta(selectedPreview.preview_json),
						primaryAction: previewPrimaryAction(selectedPreview, {
							isBusy: coachChatStore.activePreviewAction === selectedPreview.id,
							onApprove: () => approvePreview(selectedPreview),
						}),
					}}
					renderMuscleCard={() => null}
				/>
			) : null}
		</DefaultPage>
	)
})

const CoachHeaderSummary = ({ state }: { state: ChatConnectionState }) => {
	const status = getCoachHeaderStatus(state)
	const hasStatus = status.length > 0

	return (
		<div
			className={`coach-chat-header-summary is-${state}`}
			aria-label={hasStatus ? `Coach: ${status}` : 'Coach'}
		>
			<strong>Coach</strong>
			{hasStatus ? (
				<span className='coach-chat-header-status'>
					{status}
					{state === 'sending' ? (
						<i className='coach-chat-header-caret' aria-hidden='true' />
					) : null}
				</span>
			) : null}
		</div>
	)
}

const getCoachHeaderStatus = (state: ChatConnectionState): string => {
	if (state === 'connecting') return 'загружаем историю'
	if (state === 'sending') return 'печатает'
	return ''
}

const CoachSessionShortcut = ({
	state,
	isStartingNewSession,
	onStartNewSession,
}: {
	state: ChatConnectionState
	isStartingNewSession: boolean
	onStartNewSession: () => void
}) => (
	<button
		type='button'
		className='coach-chat-session-refresh'
		onClick={onStartNewSession}
		disabled={isStartingNewSession || state === 'sending'}
		aria-label='Начать новую сессию'
		title='Начать новую сессию'
	>
		{isStartingNewSession ? (
			<LoaderCircle size={15} className='coach-chat-spin' />
		) : (
			<RefreshCcw size={16} />
		)}
	</button>
)

const LoadOlderControl = ({
	hasMore,
	isLoading,
	onLoad,
}: {
	hasMore: boolean
	isLoading: boolean
	onLoad: () => void
}) => {
	if (!hasMore) {
		return null
	}

	return (
		<button
			type='button'
			className='coach-chat-load-older'
			onClick={onLoad}
			disabled={isLoading}
		>
			{isLoading ? <LoaderCircle size={15} className='coach-chat-spin' /> : null}
			Загрузить раньше
		</button>
	)
}

const MessageBlock = ({
	message,
	activePreviewAction,
	onOpenPreview,
	onRetry,
	exerciseMediaByKey,
	debugCopyText,
}: {
	message: CoachChatMessage
	activePreviewAction: string | null
	onOpenPreview: (preview: WorkoutPreviewRecord) => void
	onRetry: () => void
	exerciseMediaByKey: ExerciseMediaCache
	debugCopyText?: string | null
}) => {
	if (message.role === 'separator') {
		return <SessionSeparator label={message.text} />
	}

	if (message.status === 'pending') {
		return <StreamingMessageBlock label='Тренер печатает' />
	}

	const isUser = message.role === 'user'
	const isError = message.role === 'error' || message.status === 'failed'

	return (
		<article
			role={isError ? 'alert' : undefined}
			className={[
				'coach-chat-message',
				isUser ? 'is-user' : 'is-assistant',
				isError ? 'is-error' : '',
				message.status === 'sending' ? 'is-sending' : '',
			]
				.filter(Boolean)
				.join(' ')}
		>
			<div className='coach-chat-message-row'>
				{!isUser ? (
					<span className='coach-chat-message-avatar' aria-hidden='true'>
						<img src={CoachHeaderImage} alt='' />
					</span>
				) : null}
				<div className='coach-chat-message-stack'>
					<div className='coach-chat-message-card'>
						{isError ? (
							<div className='coach-chat-error-body'>
								<MessageText text={message.text} isUser={isUser} />
							</div>
						) : (
							<MessageText text={message.text} isUser={isUser} />
						)}
						<div className='coach-chat-message-meta'>
							{isError ? (
								<span className='coach-chat-error-status' aria-hidden='true'>
									<SadFailureIcon />
								</span>
							) : null}
							<time dateTime={message.createdAt}>
								{formatMessageTime(message.createdAt)}
							</time>
							{debugCopyText ? (
								<ChatDebugCopyButton
									text={debugCopyText}
									label={
										isError
											? 'Скопировать контекст ошибки'
											: 'Скопировать сообщение'
									}
									visibleLabel={isError ? 'Контекст' : 'Копировать'}
								/>
							) : null}
							{isError ? (
								<button
									type='button'
									className='coach-chat-error-retry'
									onClick={onRetry}
								>
									<RefreshCcw size={13} />
									Повторить
								</button>
							) : null}
							{!isUser && !isError && message.text.trim() ? (
								<ResponseActionBar onRetry={onRetry} />
							) : null}
						</div>
					</div>
				</div>
			</div>
			{message.preview ? (
				<div className='coach-chat-message-preview-lane'>
					<ActionPreviewCard
						preview={message.preview}
						isBusy={activePreviewAction === message.preview.id}
						onOpenDetails={onOpenPreview}
						exerciseMediaByKey={exerciseMediaByKey}
					/>
				</div>
			) : null}
		</article>
	)
}

const SadFailureIcon = () => (
	<svg className='coach-chat-error-face' viewBox='0 0 80 64' fill='none' focusable='false'>
		<rect x='27' y='8' width='9' height='22' rx='4.5' fill='currentColor' />
		<rect x='50' y='8' width='9' height='22' rx='4.5' fill='currentColor' />
		<path
			d='M14 51C24.5 37 55.5 37 66 51'
			stroke='currentColor'
			strokeWidth='10'
			strokeLinecap='round'
		/>
	</svg>
)

const MessageText = ({ text, isUser }: { text: string; isUser: boolean }) => {
	if (isUser) {
		return <p className='coach-chat-message-text'>{text}</p>
	}

	return (
		<ReactMarkdown
			className='coach-chat-message-markdown'
			remarkPlugins={[remarkGfm]}
			components={{
				a: ({ children, ...props }) => (
					<a {...props} target='_blank' rel='noreferrer'>
						{children}
					</a>
				),
			}}
		>
			{text}
		</ReactMarkdown>
	)
}

const SessionSeparator = ({ label }: { label: string }) => (
	<div className='coach-chat-session-separator' role='separator' aria-label={label}>
		<span>{label}</span>
	</div>
)

const THINKING_STEPS = [
	'собираю информацию',
	'смотрю историю тренировок',
	'собираю контекст',
	'анализирую состояние',
]
const THINKING_STEP_MIN_MS = 2000
const THINKING_STEP_JITTER_MS = 850

const useThinkingStep = (isThinking: boolean, fallbackLabel: string): string => {
	const [step, setStep] = useState(() => randomThinkingStep())

	useEffect(() => {
		if (!isThinking) return undefined

		let timeoutId: number | null = null
		const scheduleNext = () => {
			const delay = THINKING_STEP_MIN_MS + Math.floor(Math.random() * THINKING_STEP_JITTER_MS)
			timeoutId = window.setTimeout(() => {
				setStep(previous => randomThinkingStep(previous))
				scheduleNext()
			}, delay)
		}

		scheduleNext()
		return () => {
			if (timeoutId !== null) {
				window.clearTimeout(timeoutId)
			}
		}
	}, [isThinking])

	return isThinking ? step : fallbackLabel.toLowerCase()
}

const randomThinkingStep = (previous?: string): string => {
	const variants = previous ? THINKING_STEPS.filter(step => step !== previous) : THINKING_STEPS
	return variants[Math.floor(Math.random() * variants.length)] ?? THINKING_STEPS[0]
}

const StreamingMessageBlock = ({ label }: { label: string }) => {
	const visibleLabel = useThinkingStep(label === 'Тренер печатает', label)

	return (
		<div className='coach-chat-message is-assistant'>
			<div className='coach-chat-typing' role='status' aria-live='polite' aria-label={label}>
				<span className='coach-chat-typing-avatar' aria-hidden='true'>
					<img src={CoachHeaderImage} alt='' />
				</span>
				<div className='coach-chat-thinking' aria-hidden='true'>
					<TextShimmer
						as='span'
						duration={1.4}
						spread={3}
						className='coach-chat-thinking-text'
					>
						{visibleLabel}
					</TextShimmer>
				</div>
			</div>
		</div>
	)
}

const ResponseActionBar = ({ onRetry }: { onRetry: () => void }) => {
	return (
		<div className='coach-chat-response-actions' aria-label='Действия с ответом'>
			<button
				type='button'
				className='coach-chat-response-action is-retry'
				onClick={onRetry}
				aria-label='Повторить последний запрос'
				title='Повторить последний запрос'
			>
				<RefreshCcw size={14} />
				<span>Повторить</span>
			</button>
		</div>
	)
}

const ActionPreviewCard = ({
	preview,
	isBusy,
	onOpenDetails,
	exerciseMediaByKey,
}: {
	preview: WorkoutPreviewRecord
	isBusy: boolean
	onOpenDetails: (preview: WorkoutPreviewRecord) => void
	exerciseMediaByKey: ExerciseMediaCache
}) => {
	const body = preview.preview_json
	const exercises = uniquePreviewExercises(body.blocks.flatMap(block => block.exercises))
	const focus = focusLabel(body)
	const title = previewDisplayTitle(body, preview.title)
	const status = previewStatusText(preview.status)
	const structure = previewStructureLabel(body)
	const visibleExercises = exercises.slice(0, 5)
	const ctaLabel = preview.status === 'failed' ? status : 'Открыть'
	const tabLabel = previewTabLabel(preview.status)

	return (
		<section
			className={`coach-chat-preview is-${preview.status}`}
			aria-label={`План тренировки: ${title}`}
		>
			<div className='coach-chat-preview-topline'>
				<span
					className={`coach-chat-preview-tab is-${preview.status}`}
					aria-label={`Статус плана: ${status}`}
				>
					{tabLabel}
				</span>
			</div>
			<div
				className='coach-chat-preview-copy'
				data-duration={`${body.estimated_duration_min} мин`}
			>
				<span className='coach-chat-preview-focus'>{previewFocusLine(focus)}</span>
				<h2 className='coach-chat-preview-title'>{title}</h2>
				<p className='coach-chat-preview-structure'>{structure}</p>
			</div>
			<div className='coach-chat-preview-bottom'>
				<div
					className='coach-chat-preview-thumbs'
					aria-label='Упражнения в превью'
					tabIndex={0}
				>
					{visibleExercises.map((exercise, index) => {
						const mediaUrl = getPreviewExerciseMediaUrl(exercise, exerciseMediaByKey)
						return (
							<span
								className={[
									'coach-chat-exercise-thumb',
									mediaUrl ? 'has-media' : 'is-missing-media',
								].join(' ')}
								key={`${preview.id}-${exercise.exercise_id}-${exercise.name}`}
								title={exercise.name}
							>
								{mediaUrl ? (
									<img src={mediaUrl} alt='' loading='lazy' draggable={false} />
								) : (
									<span
										className='coach-chat-exercise-thumb-placeholder'
										aria-hidden='true'
									/>
								)}
								<small>{index + 1}</small>
							</span>
						)
					})}
				</div>
				<button
					type='button'
					className={`coach-chat-preview-go is-${preview.status}`}
					onClick={() => onOpenDetails(preview)}
					disabled={isBusy}
					aria-label={`Открыть план тренировки: ${title}`}
					title='Открыть план'
				>
					{isBusy ? <LoaderCircle size={16} className='coach-chat-spin' /> : null}
					{!isBusy && preview.status === 'approved' ? <Check size={19} /> : null}
					{!isBusy && preview.status !== 'approved' ? <ArrowRight size={20} /> : null}
					<span>{ctaLabel}</span>
				</button>
			</div>
		</section>
	)
}

const previewPrimaryAction = (
	preview: WorkoutPreviewRecord,
	{
		isBusy,
		onApprove,
	}: {
		isBusy: boolean
		onApprove: () => void
	}
) => {
	if (preview.status === 'pending_approval') {
		return {
			label: 'Создать тренировку',
			isBusy,
			onClick: onApprove,
		}
	}

	if (preview.status === 'approved') {
		return {
			label: 'Тренировка создана',
			isDisabled: true,
		}
	}

	return {
		label: previewStatusText(preview.status),
		isDisabled: true,
	}
}

const buildPreviewTraining = (
	preview: WorkoutPreviewRecord,
	exerciseMediaByKey: ExerciseMediaCache
): Training => {
	const body = preview.preview_json
	const createdAt = preview.created_at ?? new Date(0).toISOString()

	return {
		id_workout: previewWorkoutId(preview),
		name: previewDisplayTitle(body, preview.title),
		description: previewTrainingDescription(body),
		is_base: false,
		created_at: createdAt,
		media: previewCoverMedia(body, exerciseMediaByKey),
		exercises: body.blocks
			.flatMap(block => block.exercises)
			.map((exercise, index) =>
				buildPreviewTrainingExercise(exercise, index, exerciseMediaByKey)
			),
		muscle_group_codes: [],
		type_profile: {
			workout_type: 'mixed',
			exercise_type_counts: {
				cardio: 0,
				strength: body.blocks.reduce((total, block) => total + block.exercises.length, 0),
			},
		},
		history: null,
	}
}

const buildPreviewTrainingExercise = (
	exercise: PreviewExercise,
	index: number,
	exerciseMediaByKey: ExerciseMediaCache
): Training['exercises'][number] => {
	const exerciseId = getPreviewExerciseCatalogId(exercise) ?? 900000 + index
	const duration = exercise.duration_seconds ?? null
	const restTime = exercise.rest_seconds ?? null
	const reps = parsePreviewReps(exercise.reps)

	return {
		order: index + 1,
		sets: exercise.sets ?? 1,
		reps,
		duration,
		rest_time: restTime,
		exercise_detail: {
			id_exercise: exerciseId,
			type: duration && !reps ? 'cardio' : 'strength',
			name: exercise.name,
			description: exercise.notes ?? '',
			technique: exercise.notes ?? '',
			muscle_group_codes: [],
			muscle_group_ids: [],
			media: previewExerciseMedia(exercise, exerciseMediaByKey),
			is_ai_available: false,
			equipment_tags: ['no_equipment'],
		},
	}
}

const previewWorkoutId = (preview: WorkoutPreviewRecord): number => {
	const createdWorkoutId = Number(preview.created_workout_id)
	if (Number.isInteger(createdWorkoutId) && createdWorkoutId > 0) return createdWorkoutId
	return -stablePreviewNumber(preview.id)
}

const stablePreviewNumber = (value: string): number => {
	let hash = 0
	for (let index = 0; index < value.length; index += 1) {
		hash = (hash * 31 + value.charCodeAt(index)) >>> 0
	}
	return Math.max(hash, 1)
}

const previewTrainingDescription = (body: WorkoutPreview): string => {
	const parts = [
		`${body.estimated_duration_min} мин`,
		previewFocusLine(focusLabel(body)),
		previewEquipmentSummary(body.equipment),
	].filter(Boolean)
	return parts.join(' · ')
}

const previewDetailsMeta = (body: WorkoutPreview) => ({
	durationLabel: `${body.estimated_duration_min} мин`,
	equipmentLabel: previewEquipmentSummary(body.equipment) ?? 'без инвентаря',
	focusLabel: previewFocusLine(focusLabel(body)),
})

const previewEquipmentSummary = (equipment: string[]): string | null => {
	const labels = equipment
		.map(equipmentItemLabel)
		.filter(Boolean)
		.filter((label, index, list) => list.indexOf(label) === index)

	if (!labels.length) return null
	return labels.join(', ')
}

const previewCoverMedia = (
	body: WorkoutPreview,
	exerciseMediaByKey: ExerciseMediaCache
): Training['media'] | undefined => {
	const coverUrl = body.blocks
		.flatMap(block => block.exercises)
		.map(exercise => getPreviewExerciseMediaUrl(exercise, exerciseMediaByKey))
		.find(Boolean)

	if (!coverUrl) return undefined
	return { cover: [{ id: 'coach-preview-cover', url: coverUrl, ord: 0, is_primary: true }] }
}

const previewExerciseMedia = (
	exercise: PreviewExercise,
	exerciseMediaByKey: ExerciseMediaCache
): Training['exercises'][number]['exercise_detail']['media'] => {
	const mediaUrl = getPreviewExerciseMediaUrl(exercise, exerciseMediaByKey)
	if (!mediaUrl) return {}
	return {
		icon: [
			{ id: getPreviewExerciseMediaKey(exercise), url: mediaUrl, ord: 0, is_primary: true },
		],
	}
}

const parsePreviewReps = (value?: string | null): number | null => {
	if (!value) return null
	const match = value.match(/\d+/)
	if (!match) return null
	return Number(match[0])
}

const PromptChipRail = ({
	pinnedContext,
	pendingPreview,
	messages,
	onSelect,
	isBusy,
}: {
	pinnedContext: PinnedChatContext | null
	pendingPreview: WorkoutPreviewRecord | null
	messages: CoachChatMessage[]
	onSelect: (text: string) => void
	isBusy: boolean
}) => {
	const chips = buildPromptChips({ pinnedContext, pendingPreview, messages })
	if (isBusy || pendingPreview || chips.length === 0) return null

	return (
		<div className='coach-chat-chip-rail' aria-label='Подсказки'>
			{chips.map(chip => (
				<button key={chip} type='button' onClick={() => onSelect(chip)}>
					{chip}
				</button>
			))}
		</div>
	)
}

const PinnedContextAttachment = ({
	context,
	onClear,
}: {
	context: PinnedChatContext
	onClear: () => void
}) => (
	<section className='coach-chat-pinned' aria-label='Прикрепленный контекст'>
		<span className='coach-chat-pinned-mark' aria-hidden='true'>
			<CornerDownRight size={16} />
		</span>
		<div className='coach-chat-pinned-copy'>
			<span>{pinnedContextLabel(context)}</span>
			{context.justFinishedWorkout ? (
				<p>Недавно завершенная тренировка учтется в разговоре.</p>
			) : null}
		</div>
		<button type='button' onClick={onClear} aria-label='Убрать прикрепленную тренировку'>
			<X size={16} />
		</button>
	</section>
)

const Composer = ({
	value,
	isBusy,
	onChange,
	onSubmit,
}: {
	value: string
	isBusy: boolean
	onChange: (value: string) => void
	onSubmit: (event: FormEvent<HTMLFormElement>) => void
}) => {
	const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

	useLayoutEffect(() => {
		const element = textAreaRef.current
		if (!element) return
		element.style.height = 'auto'
		const maxHeight = 116
		const nextHeight = Math.min(Math.max(element.scrollHeight, 46), maxHeight)
		element.style.height = `${nextHeight}px`
		element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden'
	}, [value])

	const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (
			event.key !== 'Enter' ||
			event.shiftKey ||
			event.metaKey ||
			event.ctrlKey ||
			event.altKey ||
			event.nativeEvent.isComposing
		) {
			return
		}
		if (isBusy || !value.trim()) return

		event.preventDefault()
		event.currentTarget.form?.requestSubmit()
	}

	return (
		<form className='coach-chat-composer' onSubmit={onSubmit}>
			<textarea
				ref={textAreaRef}
				value={value}
				onChange={event => onChange(event.target.value)}
				onKeyDown={handleKeyDown}
				placeholder='Сообщение'
				disabled={isBusy}
				aria-label='Сообщение тренеру'
				rows={1}
			/>
			<button
				type='submit'
				className={['coach-chat-send', isBusy ? 'is-processing' : '']
					.filter(Boolean)
					.join(' ')}
				disabled={!value.trim() || isBusy}
				aria-busy={isBusy}
				aria-label={isBusy ? 'Отправляем сообщение' : 'Отправить'}
			>
				<span className='coach-chat-send-glyph' aria-hidden='true'>
					{isBusy ? (
						<LoaderCircle size={18} className='coach-chat-send-spinner' />
					) : (
						<ArrowUp size={25} className='coach-chat-send-arrow' />
					)}
				</span>
			</button>
		</form>
	)
}

const EmptyConversation = ({ onSelect }: { onSelect: (value: string) => void }) => (
	<section className='coach-chat-empty'>
		<div className='coach-chat-empty-copy'>
			<span>Coach</span>
			<h2>Что делаем сейчас?</h2>
			<p>Могу размять, разобрать технику или собрать короткую тренировку под самочувствие.</p>
		</div>
		<div className='coach-chat-empty-actions' aria-label='Быстрый старт'>
			{['Разминка', 'Техника', 'Тренировка'].map(action => (
				<button key={action} type='button' onClick={() => onSelect(action)}>
					{action}
				</button>
			))}
		</div>
	</section>
)

const previewStatusText = (status: string): string => {
	if (status === 'pending_approval') return 'готово'
	if (status === 'approving') return 'создаем'
	if (status === 'approved') return 'создано'
	if (status === 'cancelled') return 'отменено'
	if (status === 'superseded') return 'обновлено'
	if (status === 'stale' || status === 'expired') return 'устарело'
	if (status === 'failed') return 'ошибка'
	if (status === 'requires_revalidation') return 'нужно проверить'
	return 'на подтверждении'
}

const previewTabLabel = (status: string): string => {
	if (status === 'approved') return 'created'
	if (status === 'approving') return 'creating'
	return 'preview'
}

const previewDisplayTitle = (body: WorkoutPreview, fallbackTitle?: string): string => {
	const cleaned = cleanPreviewTitle(body.title || fallbackTitle || '')
	if (cleaned && !isGenericPreviewTitle(cleaned)) {
		return cleaned
	}
	return `${previewTitleFocus(body)} на ${durationTitleLabel(body.estimated_duration_min)}`
}

const cleanPreviewTitle = (title: string): string =>
	title
		.replace(/\s+[—-]\s*(версия\s+\d+|короче|легче)\b/gi, '')
		.replace(/\s+/g, ' ')
		.trim()

const isGenericPreviewTitle = (title: string): boolean => {
	const normalized = title.toLocaleLowerCase('ru-RU').trim()
	return [
		'персональный вариант тренировки',
		'вариант тренировки',
		'тренировка',
		'тренировка на сегодня',
		'workout',
		'workout preview',
	].includes(normalized)
}

const previewTitleFocus = (body: WorkoutPreview): string => {
	const focus = focusLabel(body)
	if (focus === 'кор') return 'Пресс и кор'
	if (focus === 'общая форма') return 'Тренировка'
	return focus.charAt(0).toLocaleUpperCase('ru-RU') + focus.slice(1)
}

const durationTitleLabel = (minutes: number): string => {
	if (minutes % 10 === 1 && minutes % 100 !== 11) return `${minutes} минуту`
	if (minutes % 10 >= 2 && minutes % 10 <= 4 && ![12, 13, 14].includes(minutes % 100)) {
		return `${minutes} минуты`
	}
	return `${minutes} минут`
}

const previewStructureLabel = (body: WorkoutPreview): string => {
	const blockCount = body.blocks.length
	const exerciseCount = uniquePreviewExercises(
		body.blocks.flatMap(block => block.exercises)
	).length
	return `${pluralRu(exerciseCount, 'упражнение', 'упражнения', 'упражнений')} · ${pluralRu(blockCount, 'блок', 'блока', 'блоков')}`
}

const pluralRu = (count: number, one: string, few: string, many: string): string => {
	const mod10 = count % 10
	const mod100 = count % 100
	const label =
		mod10 === 1 && mod100 !== 11
			? one
			: mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
				? few
				: many
	return `${count} ${label}`
}

const equipmentItemLabel = (value: string): string => {
	const labels: Record<string, string> = {
		no_equipment: 'без инвентаря',
		requires_equipment: 'инвентарь',
		mat: 'коврик',
		dumbbell: 'гантели',
		dumbbells: 'гантели',
		kettlebell: 'гиря',
		resistance_band: 'резинка',
	}
	return labels[value] ?? value.replaceAll('_', ' ')
}

const previewFocusLine = (focus: string): string =>
	focus
		.split(',')
		.map(item => item.trim())
		.filter(Boolean)
		.map(item => item.charAt(0).toLocaleUpperCase('ru-RU') + item.slice(1))
		.join(', ')

const goalLabel = (goal: string): string => {
	const labels: Record<string, string> = {
		general_fitness: 'общая форма',
		weight_loss: 'снижение веса',
		back_support: 'спина без перегруза',
		strength: 'сила',
		mobility: 'мобильность',
		conditioning: 'выносливость',
	}
	return labels[goal] ?? goal.replaceAll('_', ' ')
}

const focusLabel = (body: WorkoutPreview): string => {
	const exerciseNames = body.blocks
		.flatMap(block => block.exercises)
		.map(exercise => exercise.name.toLowerCase())
		.join(' ')
	const focusAreas: string[] = []
	if (/ягод|glute/.test(exerciseNames)) focusAreas.push('ягодицы')
	if (/пресс|кор|core|dead bug|планк|велосипед/.test(exerciseNames)) focusAreas.push('кор')
	if (/спин|back|hyperextension|кошка/.test(exerciseNames)) focusAreas.push('спина')
	if (/ног|присед|squat/.test(exerciseNames)) focusAreas.push('ноги')
	return focusAreas.length ? focusAreas.slice(0, 2).join(', ') : goalLabel(body.goal)
}

const uniquePreviewExercises = (exercises: WorkoutPreview['blocks'][number]['exercises']) => {
	const seen = new Set<string>()
	return exercises.filter(exercise => {
		const key = getPreviewExerciseMediaKey(exercise)
		if (seen.has(key)) return false
		seen.add(key)
		return true
	})
}

const collectPreviewExercises = (messages: CoachChatMessage[]): PreviewExercise[] =>
	messages.flatMap(
		message => message.preview?.preview_json.blocks.flatMap(block => block.exercises) ?? []
	)

const getPreviewExerciseMediaKey = (exercise: PreviewExercise): string => {
	const id = String(exercise.exercise_id ?? '').trim()
	if (id) return `id:${id}`
	return `name:${normalizeExerciseName(exercise.name)}`
}

const normalizeExerciseName = (name: string): string =>
	name.trim().toLocaleLowerCase('ru-RU').replaceAll('ё', 'е')

const getPreviewExerciseCatalogId = (exercise: PreviewExercise): number | null => {
	const id = Number(exercise.exercise_id)
	return Number.isInteger(id) && id > 0 ? id : null
}

const getPreviewExerciseDirectMediaUrl = (exercise: PreviewExercise): string | null => {
	const candidate = exercise as PreviewExerciseWithMedia
	return (
		candidate.thumbnail_url?.trim() ||
		candidate.image_url?.trim() ||
		getMediaRoleUrl(candidate.media, ['icon', 'cover', 'muscle_groups']) ||
		null
	)
}

const getPreviewExerciseMediaUrl = (
	exercise: PreviewExercise,
	exerciseMediaByKey: ExerciseMediaCache
): string | null =>
	getPreviewExerciseDirectMediaUrl(exercise) ??
	exerciseMediaByKey[getPreviewExerciseMediaKey(exercise)] ??
	null

const resolvePreviewExerciseMediaUrl = async (
	exercise: PreviewExercise
): Promise<string | null> => {
	const directUrl = getPreviewExerciseDirectMediaUrl(exercise)
	if (directUrl) return directUrl

	const catalogId = getPreviewExerciseCatalogId(exercise)
	if (catalogId) {
		const byId = await ExerciseProxy.search(
			createSearchParams({
				limit: 1,
				independent_fields: null,
				filter: [
					generateFilters({
						property: 'id_exercise',
						value: catalogId,
						operator: 'eq',
						independent: false,
						disableOnEmpty: true,
					}),
				],
			})
		)
		const mediaUrl = getMediaRoleUrl(byId.rows[0]?.media, ['icon', 'cover', 'muscle_groups'])
		if (mediaUrl) return mediaUrl
	}

	const name = exercise.name.trim()
	if (name.length < 2) return null

	const byName = await ExerciseProxy.FtsSearch(
		createSearchParams({
			limit: 1,
			independent_fields: null,
		}),
		name
	)
	return getMediaRoleUrl(byName.rows[0]?.media, ['icon', 'cover', 'muscle_groups']) ?? null
}

const formatMessageTime = (value: string): string => {
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}
